/**
 * App.ts — top-level UI controller. Builds the shell (HUD + operation column +
 * tabbed panels), wires the render loop and event-driven toasts, and owns the
 * settings modal.
 */
import { el, clear, Binder } from './dom';
import { type UIContext, type BuyAmount, fmt } from './context';
import { Engine } from '../core/Engine';
import { Sfx } from '../audio/sfx';
import { MineCanvas } from '../render/MineCanvas';
import { Fx } from '../render/Fx';
import { ShakeController, floatText, ripple } from './animation';
import { TooltipManager } from './tooltip';
import { Onboarding } from './onboarding';
import { showOfflineModal, showPrestigeModal, showAscendModal, showDailyModal } from './modals';
import { canClaimDaily } from '../systems/daily';
import type { OfflineSummary } from '../core/Engine';
import { unitPrice } from '../systems/economy';
import { activeEvent, activeEventRemaining } from '../systems/events';
import { formatDuration } from '../core/numbers';
import { ORE_BY_DEPTH, UPGRADES, TECHS, SKILLS, THEMES } from '../data';
import {
  upgradeUnlocked, canResearch, prestigeGain, canBuySkill, canCompleteQuest, meetsRequirement,
  GOALS, goalProgress,
} from '../systems';
import {
  buildOperation,
  panelBuildings,
  panelWorkers,
  panelUpgrades,
  panelMarket,
  panelResearch,
  panelPrestige,
  panelContracts,
  panelTrophies,
  panelStats,
  panelAscension,
  panelMastery,
  panelAutomation,
  panelChallenges,
  panelCodex,
} from './panels';

interface Tab {
  id: string;
  label: string;
  icon: string;
  build: (ctx: UIContext) => HTMLElement;
  /** If present, the tab button is hidden until this returns true. */
  unlocked?: (s: import('../core/GameState').GameState) => boolean;
}

const TABS: Tab[] = [
  { id: 'buildings', label: 'Gebäude', icon: '🏭', build: panelBuildings },
  { id: 'workers', label: 'Belegschaft', icon: '👷', build: panelWorkers },
  { id: 'upgrades', label: 'Upgrades', icon: '⬆️', build: panelUpgrades },
  { id: 'market', label: 'Markt', icon: '📈', build: panelMarket },
  { id: 'research', label: 'Forschung', icon: '🔬', build: panelResearch },
  { id: 'prestige', label: 'Prestige', icon: '✨', build: panelPrestige },
  { id: 'ascension', label: 'Aufstieg', icon: '🌌', build: panelAscension,
    unlocked: (s) => s.prestige.totalEarned > 0 || s.ascension.ascensions > 0 },
  { id: 'mastery', label: 'Meisterung', icon: '🎖️', build: panelMastery,
    unlocked: (s) => s.stats.totalOreMined >= 1e3 },
  { id: 'automation', label: 'Automatik', icon: '🤖', build: panelAutomation,
    unlocked: (s) => s.stats.lifetimeEarnings >= 5e4 || s.stats.nuggetsClicked >= 1 },
  { id: 'challenges', label: 'Herausforderungen', icon: '🎯', build: panelChallenges,
    unlocked: (s) => s.stats.lifetimeEarnings >= 1e6 },
  { id: 'contracts', label: 'Aufträge', icon: '📜', build: panelContracts },
  { id: 'trophies', label: 'Trophäen', icon: '🏆', build: panelTrophies },
  { id: 'stats', label: 'Statistik', icon: '📊', build: panelStats },
  { id: 'codex', label: 'Kodex', icon: '📖', build: panelCodex },
];

export class App {
  private binder = new Binder();
  private sfx = new Sfx();
  private ctx: UIContext;
  private buyAmount: BuyAmount = 1;
  private mineCanvas!: MineCanvas;
  private fx = new Fx();
  private shake!: ShakeController;
  private tooltips!: TooltipManager;
  private onboarding!: Onboarding;
  private gameRoot!: HTMLElement;
  private lastRender = 0;
  private activeTab = 'buildings';

  constructor(private root: HTMLElement, private engine: Engine) {
    this.ctx = {
      engine,
      bus: engine.bus,
      binder: this.binder,
      sfx: this.sfx,
      getBuyAmount: () => this.buyAmount,
      toast: (kind, msg) => this.toast(kind, msg),
      requestRebuild: () => this.binder.update(),
      confirmPrestige: (onConfirm) => showPrestigeModal(this.root, this.ctx, onConfirm),
      confirmAscend: (onConfirm) => showAscendModal(this.root, this.ctx, onConfirm),
      history: this.history,
      tooltip: (elm, getContent) => this.tooltips?.register(elm, getContent),
    };
  }

  mount(offlineMs: number, fresh = false, offlineSummary?: OfflineSummary): void {
    clear(this.root);
    this.tooltips = new TooltipManager(this.root); // before panels build (they register tips)
    const canvas = el('canvas', { class: 'mine-canvas' });
    this.mineCanvas = new MineCanvas(canvas, () => this.engine.state, () => this.engine.derived());
    (canvas as any).__mine = this.mineCanvas;

    const hud = this.buildHud();
    const operation = buildOperation(this.ctx, canvas);
    const { tabBar, panelHost } = this.buildTabs();
    const eventBanner = this.buildEventBanner();
    const toastHost = el('div', { class: 'toast-host', attrs: { role: 'status', 'aria-live': 'polite' } });
    this.toastHost = toastHost;

    this.onboarding = new Onboarding(this.ctx, {
      setTab: (id) => { this.activeTab = id; },
      onGoalComplete: () => this.fx.confetti(this.w() * 0.7, this.h() * 0.32, 40),
    });

    const colLeft = el('div', { class: 'col-left' }, operation);
    const colRight = el('div', { class: 'col-right' }, this.onboarding.tracker, tabBar, panelHost);
    // On mobile the two columns become mutually-exclusive views switched by the
    // bottom nav: 'mine' shows the operation column, any tab shows its panel.
    this.binder.toggle(colLeft, 'mobile-hidden', () => this.isMobile() && this.activeTab !== 'mine');
    this.binder.toggle(colRight, 'mobile-hidden', () => this.isMobile() && this.activeTab === 'mine');

    const gameRoot = el('div', { class: 'game' },
      hud,
      eventBanner,
      el('div', { class: 'game-body' }, colLeft, colRight),
      toastHost,
      this.buildSettingsModal(),
      this.onboarding.overlay,
    );
    this.gameRoot = gameRoot;
    this.shake = new ShakeController(gameRoot);
    this.root.appendChild(gameRoot);
    this.root.appendChild(this.fx.canvas);

    // Apply persisted theme / accessibility / volume settings.
    this.applySettings();

    // Mobile layout detection.
    if (this.isMobile()) this.activeTab = 'mine';
    this.applyLayout();
    window.addEventListener('resize', () => this.applyLayout());

    this.wireBus();
    this.lastRender = this.engine.clock.now();
    this.engine.start();
    requestAnimationFrame(() => { this.mineCanvas.resize(); this.fx.resize(); });
    // Delegated button ripple for tactile feedback (skipped under reduced-motion).
    gameRoot.addEventListener('pointerdown', (e) => {
      if (this.reducedMotion) return;
      const btn = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
      if (!btn || btn.disabled || btn.classList.contains('mine-btn')) return;
      ripple(btn, (e as PointerEvent).clientX, (e as PointerEvent).clientY);
    });
    // (resize is handled by applyLayout's listener above, which also resizes the canvases)
    window.addEventListener('beforeunload', () => this.engine.saveNow(false));
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.engine.saveNow(false);
      });
      document.addEventListener('pointerdown', () => this.sfx.unlock(), { once: true });
      this.wireHotkeys();
    }

    if (offlineMs > 10_000 && offlineSummary && (offlineSummary.oreGained > 0 || offlineSummary.moneyGained > 0)) {
      showOfflineModal(this.root, this.ctx, offlineSummary);
    } else if (offlineMs > 10_000) {
      this.toast('reward', `Willkommen zurück! Du warst ${formatDuration(offlineMs / 1000)} weg.`);
    }
    if (fresh) this.toast('success', '⛏️ Willkommen bei Mining Tycoon!');
    // Offer the daily bonus shortly after load (after any offline modal).
    if (canClaimDaily(this.engine.state, this.engine.clock.now())) {
      setTimeout(() => {
        if (canClaimDaily(this.engine.state, this.engine.clock.now())) showDailyModal(this.root, this.ctx);
      }, offlineMs > 10_000 ? 1200 : 400);
    }
  }

  /** Called each animation frame by the engine loop. */
  frame(): void {
    const now = this.engine.clock.now();
    const dt = Math.min(0.1, Math.max(0, (now - this.lastRender) / 1000));
    this.lastRender = now;
    this.binder.update();
    this.checkThemeUnlocks();
    this.sampleHistory(now);
    this.mineCanvas.render(dt);
    this.fx.render(dt);
    this.shake.apply(dt);
    this.tooltips.update();
  }

  /** Push an income + price sample roughly once per second (bounded ring). */
  private sampleHistory(now: number): void {
    if (now - this.lastSample < 1000) return;
    this.lastSample = now;
    const d = this.engine.derived();
    const income = d.miningRate * unitPrice(this.engine.state, d.focusOreId, d);
    this.history.income.push(income);
    this.history.price.push(this.engine.state.market.price[d.focusOreId] ?? 0);
    if (this.history.income.length > 120) this.history.income.shift();
    if (this.history.price.length > 120) this.history.price.shift();
  }

  // ── HUD ──
  private buildHud(): HTMLElement {
    const s = () => this.engine.state;
    // A dashboard stat-pill: icon + small label + value.
    const pill = (icon: string, label: string, valEl: HTMLElement, cls = ''): HTMLElement =>
      el('div', { class: `hud-stat ${cls}` },
        el('span', { class: 'hud-icon', text: icon }),
        el('div', { class: 'hud-meta' },
          el('span', { class: 'hud-label', text: label }),
          valEl,
        ),
      );
    const textPill = (icon: string, label: string, get: () => string, cls = ''): HTMLElement => {
      const v = el('span', { class: 'hud-val' });
      this.binder.text(v, get);
      return pill(icon, label, v, cls);
    };
    // Numeric pill with count-up animation + a "pop" when the value jumps up.
    const numPill = (icon: string, label: string, get: () => number, pre: string, suf: string, cls = ''): HTMLElement => {
      const v = el('span', { class: 'hud-val' });
      this.binder.number(v, get, (n) => pre + fmt(this.ctx, n) + suf);
      this.bindPop(v, get);
      return pill(icon, label, v, cls);
    };

    const incomePerSec = (): number => {
      const d = this.engine.derived();
      return d.miningRate * unitPrice(s(), d.focusOreId, d);
    };

    // Prestige / Ascension badge (cores hidden until earned).
    const prestige = numPill('✨', 'Prestige', () => s().prestige.points, '', '', 'pres');
    const cores = numPill('🌌', 'Kerne', () => s().ascension.cores, '', '', 'cores');
    this.binder.toggle(cores, 'hidden', () => s().ascension.cores <= 0 && s().ascension.ascensions <= 0);

    // Thin goal-progress sliver along the HUD's bottom edge.
    const goalFill = el('div', { class: 'hud-goal-fill' });
    const goalSliver = el('div', { class: 'hud-goal' }, goalFill);
    this.binder.effect(() => {
      const ob = s().onboarding;
      let pct = 100;
      if (ob.goalIndex < GOALS.length) {
        const { current, target } = goalProgress(s(), GOALS[ob.goalIndex]!);
        pct = target > 0 ? Math.max(0, Math.min(100, (current / target) * 100)) : 0;
      }
      goalFill.style.width = `${pct}%`;
    });

    return el('header', { class: 'hud' },
      el('div', { class: 'hud-logo' }, el('span', { class: 'hud-logo-mark', text: '⛏️' }), el('span', { class: 'hud-logo-text', text: 'Mining Tycoon' })),
      el('div', { class: 'hud-stats' },
        numPill('💰', 'Geld', () => s().economy.money, '$', '', 'money'),
        numPill('📈', 'Einkommen', incomePerSec, '+$', '/s', 'income'),
        numPill('🔬', 'Forschung', () => s().research.points, '', ''),
        prestige,
        cores,
        textPill('🕳️', 'Tiefe', () => `${ORE_BY_DEPTH[s().mining.focusDepth]?.name ?? '-'}`),
      ),
      el('div', { class: 'hud-spacer' }),
      el('div', { class: 'buy-modes' }, ...this.buildBuyModes()),
      this.buildDailyButton(),
      el('button', { class: 'icon-btn', text: '⚙️', title: 'Einstellungen', attrs: { 'aria-label': 'Einstellungen' }, onClick: () => this.toggleSettings(true) }),
      goalSliver,
    );
  }

  /**
   * Add a brief "pop" to a value element on a NOTABLE jump up (e.g. a sell or
   * reward) — not on the continuous income trickle. Threshold + throttle keep it
   * tasteful and cheap (reflow only when it actually pops).
   */
  private bindPop(node: HTMLElement, get: () => number): void {
    let last = get();
    let lastPop = 0;
    this.binder.effect(() => {
      const v = get();
      const now = this.engine.clock.now();
      if (v > last * 1.03 + 1 && now - lastPop > 650 && !this.reducedMotion) {
        node.classList.remove('pop');
        void node.offsetWidth;
        node.classList.add('pop');
        lastPop = now;
      }
      last = v;
    });
  }

  private buildDailyButton(): HTMLElement {
    const btn = el('button', { class: 'icon-btn daily-btn', text: '🎁', title: 'Täglicher Bonus',
      attrs: { 'aria-label': 'Täglicher Bonus' }, onClick: () => showDailyModal(this.root, this.ctx) });
    this.binder.toggle(btn, 'claimable', () => canClaimDaily(this.engine.state, this.engine.clock.now()));
    return btn;
  }

  private buildBuyModes(): HTMLElement[] {
    const modes: BuyAmount[] = [1, 10, 100, 'max'];
    return modes.map((m) => {
      const btn = el('button', { class: 'mode-btn', text: m === 'max' ? 'Max' : `×${m}`, onClick: () => {
        this.buyAmount = m;
        this.binder.update();
      } });
      this.binder.toggle(btn, 'active', () => this.buyAmount === m);
      return btn;
    });
  }

  // ── Tabs ──
  private buildTabs(): { tabBar: HTMLElement; panelHost: HTMLElement } {
    const panelHost = el('div', { class: 'panel-host' });
    const panels = new Map<string, HTMLElement>();
    for (const tab of TABS) {
      // Build each panel with a gated binder so its (often dozens of) per-frame
      // updaters are skipped entirely while the tab is hidden.
      this.ctx.binder = this.binder.gate(() => this.activeTab === tab.id);
      const p = tab.build(this.ctx);
      this.ctx.binder = this.binder;
      p.setAttribute('role', 'tabpanel');
      p.setAttribute('aria-label', tab.label);
      // The hidden toggle must always run (on the ungated binder).
      this.binder.toggle(p, 'hidden', () => this.activeTab !== tab.id);
      panels.set(tab.id, p);
      panelHost.appendChild(p);
    }
    // Mobile-only "Mine" home button (shows the operation column).
    const mineTab = el('button', { class: 'tab-btn tab-mine', title: 'Mine' },
      el('span', { class: 'tab-icon', text: '⛏️' }),
      el('span', { class: 'tab-label', text: 'Mine' }),
    );
    mineTab.addEventListener('click', () => { this.activeTab = 'mine'; this.binder.update(); this.sfx.play('click'); });
    this.binder.toggle(mineTab, 'active', () => this.activeTab === 'mine');

    mineTab.setAttribute('role', 'tab');
    this.binder.effect(() => mineTab.setAttribute('aria-selected', String(this.activeTab === 'mine')));

    const tabBar = el('div', { class: 'tab-bar', attrs: { role: 'tablist', 'aria-label': 'Game sections' } },
      mineTab,
      ...TABS.map((tab) => {
        const btn = el('button', { class: 'tab-btn', title: tab.label, attrs: { role: 'tab', 'aria-label': tab.label } },
          el('span', { class: 'tab-icon', text: tab.icon }),
          el('span', { class: 'tab-label', text: tab.label }),
        );
        btn.addEventListener('click', () => { this.activeTab = tab.id; this.binder.update(); this.sfx.play('click'); });
        this.binder.toggle(btn, 'active', () => this.activeTab === tab.id);
        this.binder.effect(() => btn.setAttribute('aria-selected', String(this.activeTab === tab.id)));
        if (tab.unlocked) this.binder.toggle(btn, 'hidden', () => !tab.unlocked!(this.engine.state));
        // Actionable badge: a number for contracts, a "!" dot otherwise.
        const badge = el('span', { class: 'tab-badge' });
        this.binder.text(badge, () => this.tabBadge(tab.id));
        this.binder.toggle(badge, 'hidden', () => this.tabBadge(tab.id) === '');
        this.binder.toggle(badge, 'dot', () => this.tabBadge(tab.id) === '!');
        btn.appendChild(badge);
        return btn;
      }),
    );

    // Sliding active-tab indicator (glides under the selected tab).
    const indicator = el('div', { class: 'tab-indicator' });
    tabBar.appendChild(indicator);
    let lastActive = '';
    this.binder.effect(() => {
      if (this.activeTab === lastActive && !this.repositionTab) return;
      const btn = tabBar.querySelector('.tab-btn.active') as HTMLElement | null;
      if (btn && btn.offsetWidth > 0) {
        indicator.style.width = `${btn.offsetWidth}px`;
        indicator.style.transform = `translateX(${btn.offsetLeft}px)`;
        indicator.style.opacity = '1';
        lastActive = this.activeTab;
        this.repositionTab = false;
      }
    });
    return { tabBar, panelHost };
  }

  private repositionTab = true;

  private isMobile(): boolean {
    return ((typeof window !== 'undefined' && window.innerWidth) || 1024) <= 760;
  }

  /** Toggle the mobile root class and refresh layout-dependent bindings. */
  private applyLayout(): void {
    this.gameRoot.classList.toggle('mobile', this.isMobile());
    this.mineCanvas.resize();
    this.fx.resize();
    this.repositionTab = true; // tab widths/positions may have shifted
    this.binder.update();
  }

  /** Keyboard shortcuts: Space = mine, S = sell all, 1-9 = switch tab. */
  private wireHotkeys(): void {
    document.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.code === 'Space') {
        e.preventDefault();
        (this.gameRoot.querySelector('.mine-btn') as HTMLButtonElement | null)?.click();
      } else if (e.key === 's' || e.key === 'S') {
        (this.gameRoot.querySelector('.sell-btn') as HTMLButtonElement | null)?.click();
      } else if (/^[1-9]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        const tab = TABS[idx];
        if (tab) { this.activeTab = tab.id; this.binder.update(); }
      }
    });
  }

  /** Actionable indicator for a tab: a count for contracts, '!' dot for others, or ''. */
  private tabBadge(tabId: string): string {
    const s = this.engine.state;
    switch (tabId) {
      case 'upgrades':
        return UPGRADES.some((u) => upgradeUnlocked(s, u.id) && s.economy.money >= u.cost) ? '!' : '';
      case 'research':
        return TECHS.some((t) => canResearch(s, t.id)) ? '!' : '';
      case 'prestige':
        return prestigeGain(s) > 0 || SKILLS.some((sk) => canBuySkill(s, sk.id)) ? '!' : '';
      case 'contracts': {
        const completable = s.quests.active.filter((q) => canCompleteQuest(s, q.id)).length;
        if (completable > 0) return String(completable);
        return s.quests.active.length > 0 ? '!' : '';
      }
      default:
        return '';
    }
  }

  private buildEventBanner(): HTMLElement {
    const banner = el('div', { class: 'event-banner' });
    this.binder.effect(() => {
      const ev = activeEvent(this.engine.state);
      if (!ev) { banner.classList.add('hidden'); return; }
      banner.classList.remove('hidden');
      banner.classList.toggle('bad', ev.kind === 'bad');
      const remain = activeEventRemaining(this.engine.state) / 1000;
      banner.textContent = `${ev.kind === 'good' ? '🎉' : '⚠️'} ${ev.name} — ${ev.description}  (${Math.ceil(remain)}s)`;
    });
    return banner;
  }

  // ── Toasts ──
  private toastHost!: HTMLElement;
  private toast(kind: string, message: string): void {
    if (!this.toastHost) return;
    const icons: Record<string, string> = { info: 'ℹ️', success: '✅', warning: '⚠️', reward: '🎁' };
    const t = el('div', { class: `toast ${kind}` },
      el('span', { class: 'toast-icon', text: icons[kind] ?? '•' }),
      el('span', { class: 'toast-msg', text: message }),
      el('div', { class: 'toast-bar' }),
    );
    this.toastHost.appendChild(t);
    requestAnimationFrame(() => t.classList.add('show'));
    setTimeout(() => { t.classList.remove('show'); setTimeout(() => t.remove(), 300); }, 3600);
    // Keep the stack bounded.
    while (this.toastHost.children.length > 5) this.toastHost.firstChild?.remove();
  }

  private wireBus(): void {
    const bus = this.engine.bus;
    bus.on('notify', ({ kind, message }) => this.toast(kind, message));
    bus.on('save', ({ manual }) => { if (manual) this.toast('info', '💾 Spiel gespeichert'); });

    // ── Juice: celebratory effects on impactful events (gated by reduced-motion) ──
    bus.on('prestige', () => { this.confetti(undefined, undefined, 140); this.coins(undefined, undefined, 50); this.kick(0.9); });
    bus.on('ascend', () => { this.confetti(undefined, undefined, 220); this.coins(undefined, undefined, 70); this.kick(1); });
    bus.on('achievement:earned', () => { this.confetti(undefined, this.h() * 0.28, 70); this.kick(0.35); });
    bus.on('layer:unlocked', () => {
      const r = this.canvasCenter();
      this.ring(r.x, r.y, '#ffce82', 220); this.confetti(r.x, r.y, 60); this.kick(0.5);
    });
    bus.on('quest:completed', () => { this.coins(this.w() * 0.7, this.h() * 0.5, 28); this.kick(0.25); });
    bus.on('ability:activated', () => { this.confetti(this.w() / 2, this.h() * 0.4, 40); this.kick(0.4); });
    bus.on('nugget:claimed', () => {
      const r = this.canvasCenter();
      this.coins(r.x, r.y, 26); this.ring(r.x, r.y, '#ffd25a', 140); this.kick(0.3);
    });
    bus.on('event:started', () => {
      const ev = activeEvent(this.engine.state);
      if (ev?.kind === 'bad') this.kick(0.6);
      else this.confetti(this.w() / 2, this.h() * 0.3, 50);
    });
    bus.on('sell', ({ value }) => {
      if (value <= 0) return;
      // Prefer the Sell-All button; fall back to the mine button. Skip if hidden (e.g. mobile panel view).
      const sell = this.gameRoot.querySelector('.sell-btn') as HTMLElement | null;
      const anchor = sell && sell.offsetParent !== null ? sell : (this.gameRoot.querySelector('.mine-btn') as HTMLElement | null);
      if (anchor && anchor.offsetParent !== null) floatText(anchor, `+$${fmt(this.ctx, value)}`, 'gain');
    });
  }

  // ── Reduced-motion / particle-density aware juice ──
  private reducedMotion = false;
  private density = 1;
  // Transient sparkline history (~1 Hz, last ~120 samples).
  private history = { income: [] as number[], price: [] as number[] };
  private lastSample = 0;
  private confetti(x: number | undefined, y: number | undefined, count: number): void {
    if (this.reducedMotion) return;
    this.fx.confetti(x, y, Math.max(4, Math.round(count * this.density)));
  }
  private coins(x: number | undefined, y: number | undefined, count: number): void {
    if (this.reducedMotion) return;
    this.fx.coins(x, y, Math.max(3, Math.round(count * this.density)));
  }
  private ring(x: number, y: number, color: string, max: number): void {
    if (this.reducedMotion) return;
    this.fx.ring(x, y, color, max);
  }
  private kick(n: number): void {
    if (this.reducedMotion) return;
    this.shake.kick(n);
  }

  /** Apply theme, reduced-motion, font scale, particle density, and volumes from settings. */
  private applySettings(): void {
    const st = this.engine.state.settings;
    for (const t of THEMES) this.gameRoot.classList.remove(`theme-${t.id}`);
    this.gameRoot.classList.add(`theme-${st.theme}`);
    this.gameRoot.classList.toggle('reduced-motion', st.reducedMotion);
    this.gameRoot.style.fontSize = `${(15 * st.fontScale).toFixed(2)}px`;
    this.reducedMotion = st.reducedMotion;
    if (this.shake) this.shake.reducedMotion = st.reducedMotion;
    if (this.mineCanvas) { this.mineCanvas.reducedMotion = st.reducedMotion; this.mineCanvas.density = st.particleDensity; }
    this.density = st.particleDensity;
    this.sfx.setVolumes({
      master: st.masterVolume, music: st.musicVolume, sfx: st.sfxVolume,
      musicEnabled: st.musicEnabled, muted: st.muted,
    });
  }

  /** Unlock themes whose progress requirement is now met. */
  private checkThemeUnlocks(): void {
    const st = this.engine.state;
    for (const t of THEMES) {
      if (!st.settings.unlockedThemes.includes(t.id) && meetsRequirement(st, t.requires)) {
        st.settings.unlockedThemes.push(t.id);
        this.toast('success', `🎨 Design freigeschaltet: ${t.name}`);
      }
    }
  }

  private w(): number { return (typeof window !== 'undefined' && window.innerWidth) || 800; }
  private h(): number { return (typeof window !== 'undefined' && window.innerHeight) || 600; }
  private canvasCenter(): { x: number; y: number } {
    const c = this.gameRoot.querySelector('.mine-canvas') as HTMLElement | null;
    if (!c || !c.getBoundingClientRect) return { x: this.w() / 2, y: this.h() / 2 };
    const r = c.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  // ── Settings modal ──
  private modal!: HTMLElement;
  private buildSettingsModal(): HTMLElement {
    const s = () => this.engine.state.settings;
    const exportArea = el('textarea', { class: 'settings-area', placeholder: 'Exportierter Spielstand erscheint hier · zum Importieren einfügen' }) as HTMLTextAreaElement;

    const boolRow = (label: string, get: () => boolean, set: (v: boolean) => void): HTMLElement => {
      const input = el('input', { type: 'checkbox', onChange: (e) => { set((e.target as HTMLInputElement).checked); this.applySettings(); } }) as HTMLInputElement;
      const row = el('label', { class: 'toggle setting-row' }, input, el('span', { text: label }));
      this.binder.effect(() => { if (input.checked !== get()) input.checked = get(); });
      return row;
    };
    const sliderRow = (label: string, get: () => number, set: (v: number) => void, min = 0, max = 1, step = 0.05): HTMLElement => {
      const input = el('input', { type: 'range', onInput: (e) => { set(parseFloat((e.target as HTMLInputElement).value)); this.applySettings(); } }) as HTMLInputElement;
      input.min = String(min); input.max = String(max); input.step = String(step);
      input.value = String(get());
      return el('div', { class: 'setting-row slider-row' }, el('span', { class: 'setting-label', text: label }), input);
    };

    // Theme picker.
    const themeRow = el('div', { class: 'theme-row' });
    for (const t of THEMES) {
      const btn = el('button', { class: 'theme-chip', title: t.name, text: t.icon, onClick: () => {
        if (!s().unlockedThemes.includes(t.id)) return;
        s().theme = t.id; this.applySettings();
      } });
      this.binder.toggle(btn, 'active', () => s().theme === t.id);
      this.binder.toggle(btn, 'locked', () => !s().unlockedThemes.includes(t.id));
      themeRow.appendChild(btn);
    }

    const notationSel = el('select', { class: 'setting-select', onChange: (e) => {
      s().notation = (e.target as HTMLSelectElement).value as any; this.applySettings();
    } },
      ...[['short','Kurz'],['scientific','Wissenschaftlich'],['engineering','Technisch'],['letters','Buchstaben']].map(([v,l]) =>
        el('option', { value: v, text: l })),
    ) as HTMLSelectElement;
    this.binder.effect(() => { if (notationSel.value !== s().notation) notationSel.value = s().notation; });

    const modal = el('div', { class: 'modal-overlay hidden' },
      el('div', { class: 'modal settings-modal' },
        el('div', { class: 'modal-head' }, el('h2', { text: '⚙️ Einstellungen' }),
          el('button', { class: 'icon-btn', text: '✕', onClick: () => this.toggleSettings(false) })),

        el('div', { class: 'settings-section-title', text: '🔊 Audio' }),
        boolRow('Alles stumm', () => s().muted, (v) => (s().muted = v)),
        boolRow('Musik', () => s().musicEnabled, (v) => (s().musicEnabled = v)),
        sliderRow('Gesamt', () => s().masterVolume, (v) => (s().masterVolume = v)),
        sliderRow('Musik-Lautst.', () => s().musicVolume, (v) => (s().musicVolume = v)),
        sliderRow('SFX-Lautst.', () => s().sfxVolume, (v) => (s().sfxVolume = v)),

        el('div', { class: 'settings-section-title', text: '🎨 Anzeige' }),
        el('div', { class: 'setting-row' }, el('span', { class: 'setting-label', text: 'Design' }), themeRow),
        el('div', { class: 'setting-row' }, el('span', { class: 'setting-label', text: 'Zahlenformat' }), notationSel),
        boolRow('Reduzierte Bewegung', () => s().reducedMotion, (v) => (s().reducedMotion = v)),
        sliderRow('Partikeldichte', () => s().particleDensity, (v) => (s().particleDensity = v)),
        sliderRow('Schriftgröße', () => s().fontScale, (v) => (s().fontScale = v), 0.85, 1.4, 0.05),

        el('div', { class: 'settings-section-title', text: '💾 Daten' }),
        el('div', { class: 'settings-actions' },
          el('button', { class: 'op-btn', text: '💾 Speichern', onClick: () => this.engine.saveNow(true) }),
          el('button', { class: 'op-btn', text: '📤 Exportieren', onClick: () => { exportArea.value = this.engine.save.export(this.engine.state); } }),
          el('button', { class: 'op-btn', text: '📥 Importieren', onClick: () => this.doImport(exportArea.value) }),
        ),
        exportArea,
        el('div', { class: 'settings-actions two' },
          el('button', { class: 'op-btn', text: '🎓 Tutorial wiederholen', onClick: () => { this.onboarding.replay(); this.toggleSettings(false); } }),
          el('button', { class: 'op-btn danger', text: '⚠️ Komplett zurücksetzen', onClick: () => this.doHardReset() }),
        ),
        el('div', { class: 'settings-foot', text: 'Mining Tycoon · speichert lokal im Browser' }),
      ),
    );
    modal.addEventListener('click', (e) => { if (e.target === modal) this.toggleSettings(false); });
    this.modal = modal;
    return modal;
  }

  private toggleSettings(open: boolean): void {
    this.modal.classList.toggle('hidden', !open);
  }

  private doImport(text: string): void {
    if (!text.trim()) return;
    try {
      const state = this.engine.save.import(text, this.engine.clock.now());
      this.engine.replaceState(state); // in-place: keeps the loop's reference valid
      this.engine.saveNow(false);
      this.toast('success', 'Stand importiert — lädt neu …');
      if (typeof location !== 'undefined' && location.reload) setTimeout(() => location.reload(), 600);
    } catch {
      this.toast('warning', 'Ungültiger Spielstand');
      this.sfx.play('error');
    }
  }

  private doHardReset(): void {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('WIRKLICH den gesamten Fortschritt löschen?')) return;
    this.engine.hardReset();
    if (typeof location !== 'undefined') location.reload();
  }
}
